import { SVGProps } from 'react';

import { Flash, Gear } from '@r-client/shared/ui/icons';
import { DIGITAL_ASSETS_PORTFOLIO_ROUTE } from '@r-client/wallet/util/constants';

import Accredited from './images/logo-accredited.svg';
import Crypto from './images/logo-crypto.svg';
import NoteLogoFull from './images/logo-r-note.svg';
import NoteLogo from './images/logo-r-note-small.svg';
import RealEstate from './images/logo-real-estate.svg';
import VideoGames from './images/logo-video-games.svg';
import Wallet from './images/logo-wallet.svg';
import Startup from './images/startup.svg';

export type TNavigationItem =
  | {
      href: string;
      title: string;
      image?: string;
      fullImage?: string;
      description?: string;
      icon?: React.FC<React.PropsWithChildren<SVGProps<SVGSVGElement>>>;
      nested?: boolean;
      items?: TNavigationItem[];
      trackingParams?: Record<string, string>;
    }
  | undefined;

export const investNavItems = (): TNavigationItem[] => [
  {
    href: '/investment-opportunities',
    title: 'Startups',
    image: Startup,
    description: 'Invest in vetted private companies',
  },
  {
    href: '/real-estate',
    title: 'Real estate',
    image: RealEstate,
    description: 'Build a global real estate portfolio',
  },
  {
    href: '/culture',
    title: 'Culture',
    image: VideoGames,
    description: 'Invest in what inspires you',
  },
  {
    href: '/crypto',
    title: 'Crypto',
    image: Crypto,
    description: 'Invest in blockchain tech and digital assets',
  },
  {
    href: '/accredited',
    title: 'Deal Room',
    image: Accredited,
    description: 'Access the deal room for accredited investors',
  },
  {
    href: '/wallet',
    title: 'Wallet',
    image: Wallet,
    description: 'Store and manage your investments',
    trackingParams: {
      menu_entry: 'wallet',
      platform: 'web',
    },
  },
  {
    href: '/note',
    title: 'Republic Note',
    image: NoteLogo,
    fullImage: NoteLogoFull,
  },
];

export const featuredInvestorsNavItem: TNavigationItem = {
  href: '/featured-investors',
  title: 'Featured investors',
};

export const secondariesMarketNavItem = {
  href: '/market',
  title: 'Buy & sell assets on the secondary market',
  mobileTitle: 'Buy & sell assets on the secondary market',
};

export const learnNavItems: TNavigationItem[] = [
  {
    href: '/about',
    title: 'About',
  },
  {
    href: '/learn/investors/how-it-works',
    title: 'How it works',
  },
  {
    href: '/investors/why-invest',
    title: 'Why invest',
  },
  {
    href: '/help/category/investing',
    title: 'FAQ',
  },
  {
    href: '/insights',
    nested: true,
    title: 'Insights',
    items: [
      {
        href: '/insights/founders',
        title: 'Founders',
      },
      {
        href: '/insights/digital-assets',
        title: 'Digital assets',
      },
      {
        href: '/insights/learn',
        title: 'Learn',
      },
      {
        href: '/insights/real-estate',
        title: 'Real estate',
      },
    ],
  },
  {
    href: '/events',
    title: 'Events',
  },
];

export const raiseCapitalNavItems: TNavigationItem[] = [
  {
    href: '/raise',
    title: 'Raise capital',
  },
  {
    href: '/tokenization',
    title: 'Tokenization',
  },
  {
    href: '/crypto/services',
    title: 'Advisory services',
  },
  {
    href: '/sharedrops',
    title: `Sharedrops™`,
  },
  {
    href: '/partnerships',
    title: 'Partner network',
  },
  {
    href: '/insights/founders',
    title: 'Insights',
  },
];

export const userNavItems = ({
  userSlug,
  mobile,
  isFounder,
  hasWallet,
  hasAllowedOktaDomain,
}: {
  userSlug: string;
  mobile: boolean;
  isFounder?: boolean;
  hasWallet?: boolean;
  republicWalletCta?: boolean;
  hasAllowedOktaDomain?: boolean;
}): TNavigationItem[] => [
  hasAllowedOktaDomain
    ? {
        href: `/admin`,
        title: 'Admin Dash',
      }
    : undefined,
  {
    href: `/@${userSlug}`,
    title: 'My profile',
  },
  isFounder
    ? {
        href: '/campaigns',
        title: 'My campaigns',
      }
    : undefined,
  {
    href: `/portfolio`,
    title: 'My portfolio',
  },
  hasWallet
    ? {
        href: DIGITAL_ASSETS_PORTFOLIO_ROUTE,
        title: 'My wallet',
      }
    : {
        href: '/wallet',
        title: 'My wallet',
        trackingParams: {
          menu_entry: 'wallet',
          platform: 'web',
        },
      },
  {
    href: `/saved`,
    title: 'Saved deals',
  },
  {
    href: `/invite`,
    title: 'Invite friends',
  },
  ...(mobile
    ? [
        {
          href: `/notifications`,
          title: 'Notifications',
          icon: Flash,
        },
      ]
    : []),
  {
    href: `/settings`,
    title: 'Settings',
    icon: Gear,
  },
  {
    href: '/logout',
    title: 'Logout',
  },
];
